/**
 * Custom error handling utility for Flows.Cash
 * Provides consistent error handling across the application
 */

import { PostgrestError } from '@supabase/supabase-js';

// Error codes
export enum ErrorCode {
  // Authentication errors
  AUTH_INVALID_EMAIL = 'auth/invalid-email',
  AUTH_USER_DISABLED = 'auth/user-disabled',
  AUTH_USER_NOT_FOUND = 'auth/user-not-found',
  AUTH_WRONG_PASSWORD = 'auth/wrong-password',
  AUTH_EMAIL_ALREADY_IN_USE = 'auth/email-already-in-use',
  AUTH_WEAK_PASSWORD = 'auth/weak-password',
  AUTH_REQUIRES_RECENT_LOGIN = 'auth/requires-recent-login',
  AUTH_POPUP_CLOSED = 'auth/popup-closed-by-user',
  AUTH_CANCELLED = 'auth/cancelled',
  AUTH_NETWORK_ERROR = 'auth/network-error',
  
  // Database errors
  DB_DOCUMENT_NOT_FOUND = 'db/document-not-found',
  DB_PERMISSION_DENIED = 'db/permission-denied',
  DB_UNAVAILABLE = 'db/unavailable',
  DB_TRANSACTION_FAILED = 'db/transaction-failed',
  
  // Storage errors
  STORAGE_OBJECT_NOT_FOUND = 'storage/object-not-found',
  STORAGE_UNAUTHORIZED = 'storage/unauthorized',
  STORAGE_CANCELED = 'storage/canceled',
  
  // Network errors
  NETWORK_ERROR = 'network/error',
  NETWORK_TIMEOUT = 'network/timeout',
  
  // General errors
  UNKNOWN_ERROR = 'unknown/error',
  NOT_IMPLEMENTED = 'general/not-implemented',
  INVALID_ARGUMENT = 'general/invalid-argument',
  OPERATION_NOT_ALLOWED = 'general/operation-not-allowed',
  
  // New errors from the code block
  UNAUTHENTICATED = 'unauthenticated',
  UNAUTHORIZED = 'unauthorized',
  INVALID_CREDENTIALS = 'invalid_credentials',
  EMAIL_EXISTS = 'email_exists',
  WEAK_PASSWORD = 'weak_password',
  
  // Database errors
  NOT_FOUND = 'not_found',
  DUPLICATE_ENTRY = 'duplicate_entry',
  FOREIGN_KEY_VIOLATION = 'foreign_key_violation',
  CONSTRAINT_VIOLATION = 'constraint_violation',
  
  // Service errors
  SERVICE_UNAVAILABLE = 'service_unavailable',
  RATE_LIMITED = 'rate_limited',
}

// Error categories
export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  DATABASE = 'database',
  STORAGE = 'storage',
  NETWORK = 'network',
  GENERAL = 'general'
}

// Error severity
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Application error interface
export interface AppError {
  code: ErrorCode | string;
  message: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  originalError?: any;
  timestamp: Date;
  data?: any;
  name: string;
}

/**
 * Custom error class for application-specific errors
 */
export class AppError extends Error {
  code: string;
  httpStatus?: number;
  
  constructor(message: string, code: string, httpStatus?: number) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

/**
 * Creates a standardized application error
 * @param code Error code
 * @param message User-friendly error message
 * @param category Error category
 * @param severity Error severity
 * @param originalError Original error object
 * @param data Additional error data
 * @returns Standardized AppError object
 */
export function createError(
  code: ErrorCode | string,
  message: string,
  category: ErrorCategory = ErrorCategory.GENERAL,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  originalError?: any,
  data?: any
): AppError {
  return {
    code,
    message,
    category,
    severity,
    originalError,
    timestamp: new Date(),
    data,
    name: 'AppError'
  };
}

/**
 * Maps Supabase PostgreSQL error codes to application error codes
 */
const postgresErrorCodeMap: Record<string, ErrorCode> = {
  '23505': ErrorCode.DUPLICATE_ENTRY, // Unique violation
  '23503': ErrorCode.FOREIGN_KEY_VIOLATION, // Foreign key violation
  '23514': ErrorCode.CONSTRAINT_VIOLATION, // Check constraint violation
  '42P01': ErrorCode.NOT_FOUND, // Table does not exist
  '42703': ErrorCode.NOT_FOUND, // Column does not exist
  '3D000': ErrorCode.NOT_FOUND, // Database does not exist
  '22023': ErrorCode.CONSTRAINT_VIOLATION, // Invalid parameter value
  '22P02': ErrorCode.CONSTRAINT_VIOLATION, // Invalid text representation
};

/**
 * Maps HTTP status codes to application error codes
 */
const httpStatusCodeMap: Record<number, ErrorCode> = {
  401: ErrorCode.UNAUTHENTICATED,
  403: ErrorCode.UNAUTHORIZED,
  404: ErrorCode.NOT_FOUND,
  409: ErrorCode.DUPLICATE_ENTRY,
  429: ErrorCode.RATE_LIMITED,
  500: ErrorCode.SERVICE_UNAVAILABLE,
  503: ErrorCode.SERVICE_UNAVAILABLE
};

/**
 * Handles Supabase errors and returns a standardized AppError
 */
export function handleSupabaseError(error: unknown): AppError {
  // If it's already an AppError, return it as is
  if (error instanceof AppError) {
    return error;
  }
  
  // Handle PostgrestError
  if (error instanceof PostgrestError) {
    const code = error.code;
    let errorCode = ErrorCode.UNKNOWN_ERROR;
    let message = error.message || 'An unknown database error occurred';
    
    // Check if we have a mapping for this postgres error code
    if (code && postgresErrorCodeMap[code]) {
      errorCode = postgresErrorCodeMap[code];
      
      // Provide more user-friendly messages for common errors
      switch (errorCode) {
        case ErrorCode.DUPLICATE_ENTRY:
          message = 'This record already exists';
          break;
        case ErrorCode.FOREIGN_KEY_VIOLATION:
          message = 'This operation would break data relationships';
          break;
        case ErrorCode.CONSTRAINT_VIOLATION:
          message = 'Invalid data provided';
          break;
        case ErrorCode.NOT_FOUND:
          message = 'The requested resource was not found';
          break;
      }
    }
    
    // Check HTTP status code
    const details = error.details as { statusCode?: number } | undefined;
    if (details?.statusCode && httpStatusCodeMap[details.statusCode]) {
      errorCode = httpStatusCodeMap[details.statusCode];
    }
    
    return new AppError(message, errorCode, details?.statusCode);
  }
  
  // Handle authentication errors from Supabase
  if (
    error instanceof Error && 
    (error.message.includes('auth') || error.message.includes('JWT'))
  ) {
    if (error.message.includes('email already exists')) {
      return new AppError('This email is already registered', ErrorCode.EMAIL_EXISTS, 409);
    }
    
    if (error.message.includes('password') && error.message.includes('weak')) {
      return new AppError('Password is too weak', ErrorCode.WEAK_PASSWORD, 400);
    }
    
    if (error.message.includes('invalid credentials')) {
      return new AppError('Invalid email or password', ErrorCode.INVALID_CREDENTIALS, 401);
    }
    
    if (error.message.includes('JWT')) {
      return new AppError('Your session has expired. Please sign in again.', ErrorCode.UNAUTHENTICATED, 401);
    }
    
    return new AppError('Authentication error', ErrorCode.UNAUTHENTICATED, 401);
  }
  
  // Handle network errors
  if (error instanceof Error && (
    error.message.includes('Failed to fetch') || 
    error.message.includes('Network Error') ||
    error.message.includes('network request failed')
  )) {
    return new AppError(
      'Network error. Please check your connection and try again.',
      ErrorCode.NETWORK_ERROR,
      0
    );
  }
  
  // Generic error handling
  const message = error instanceof Error ? error.message : 'An unknown error occurred';
  return new AppError(message, ErrorCode.UNKNOWN_ERROR);
}

/**
 * Handles errors in a consistent way across the application
 * @param error Error to handle
 * @param defaultMessage Default message to show if error can't be parsed
 * @returns Standardized AppError object
 */
export function handleError(error: any, defaultMessage = 'An unexpected error occurred'): AppError {
  console.error('Error caught:', error);
  
  // If it's already an AppError, return it
  if (error && error.code && error.message && error.category && error.severity) {
    return error as AppError;
  }
  
  // Handle Supabase errors
  if (error && (error.code || error.message || error.statusCode || error.error_description)) {
    return handleSupabaseError(error);
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return createError(
      ErrorCode.UNKNOWN_ERROR,
      error.message || defaultMessage,
      ErrorCategory.GENERAL,
      ErrorSeverity.ERROR,
      error
    );
  }
  
  // Handle unknown errors
  return createError(
    ErrorCode.UNKNOWN_ERROR,
    typeof error === 'string' ? error : defaultMessage,
    ErrorCategory.GENERAL,
    ErrorSeverity.ERROR,
    error
  );
}

/**
 * Gets a user-friendly error message
 * @param error Error to get message from
 * @param defaultMessage Default message to show if error can't be parsed
 * @returns User-friendly error message
 */
export function getErrorMessage(error: any, defaultMessage = 'An unexpected error occurred'): string {
  const appError = handleError(error, defaultMessage);
  return appError.message;
}

/**
 * Logs an error to the console and potentially to a logging service
 * @param error Error to log
 * @param context Additional context information
 */
export function logError(error: any, context?: string): void {
  const appError = handleError(error);
  
  console.error(`[${appError.category.toUpperCase()}] ${context ? `(${context}) ` : ''}${appError.message}`, {
    code: appError.code,
    severity: appError.severity,
    timestamp: appError.timestamp,
    originalError: appError.originalError
  });
  
  // TODO: Add integration with external logging service if needed
}

/**
 * Determines if an error should be reported to the user
 * @param error Error to check
 * @returns True if the error should be reported to the user
 */
export function shouldReportToUser(error: any): boolean {
  const appError = handleError(error);
  
  // Don't report network errors if they're just informational
  if (appError.category === ErrorCategory.NETWORK && appError.severity === ErrorSeverity.INFO) {
    return false;
  }
  
  // Don't report canceled operations
  if (appError.code === ErrorCode.AUTH_CANCELLED || appError.code === ErrorCode.STORAGE_CANCELED) {
    return false;
  }
  
  return true;
}

/**
 * Returns a user-friendly error message
 */
export function getFriendlyErrorMessage(error: unknown): string {
  const appError = handleSupabaseError(error);
  return appError.message;
} 